import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import {
  ShieldCheck,
  Shield,
  FileText,
  CheckCircle2,
  Plus,
  BookOpen,
  Wand2,
  Brain,
  Check,
  Flag,
  Trash2,
  Pencil,
  SlidersHorizontal,
  RotateCcw,
  ChevronDown,
  ChevronRight,
  Search,
  Loader2,
  AlertTriangle,
  X,
  Copy,
} from "lucide-react";
import { cn } from "../../../lib/utils";
import { Button } from "../../../ui/button";
import { Checkbox } from "../../../ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "../../../ui/dialog";
import { Popover, PopoverTrigger, PopoverContent } from "../../../ui/popover";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../ui/table";
import { GatewayPostProcessorApi } from "../Api/gatewayApi";
import { formatTimestamp, apiError } from "../utils/gatewayUtils";
import GatewaySensitivityBadge, { sensitivityLabel } from "../components/GatewaySensitivityBadge";
import GatewayProtocolBadge from "../components/GatewayProtocolBadge";
import GatewayCapabilityTypeBadge from "../components/GatewayCapabilityTypeBadge";
import GatewayJsonViewer from "../components/GatewayJsonViewer";
import GatewayChatPanel from "../components/GatewayChatPanel";
import { ChatMessage } from "../interfaces/gatewayInterfaces";
import { useToast } from "../../../../hooks/use-toast";
import "../../ManagementDialog.css";
import "../../CloudAnalytics/CloudEntityAnalytics.css";
import "../AgenticGateway.css";

const SENS_OPTIONS = ["PUBLIC", "INTERNAL", "CONFIDENTIAL", "RESTRICTED"];
const CONTEXT_KEYS = ["", "ssn", "card", "secret", "phone", "iban"];
const FACETS: { id: string; label: string; key: string }[] = [
  { id: "sensitivity", label: "Sensitivity", key: "sensitivities" },
  { id: "category", label: "Category", key: "categories" },
  { id: "capabilityType", label: "Type", key: "capabilityTypes" },
  { id: "protocol", label: "Protocol", key: "protocols" },
  { id: "producer", label: "Producer", key: "producers" },
];
const inputCls =
  "px-3 py-2 rounded-lg border border-border ag-surface text-sm text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:border-primary/50 transition-colors";

// Badged checkbox — when checked, a light primary tint + primary check (not a solid blue block).
const CHECKBOX_CLS =
  "border-primary/60 data-[state=checked]:bg-primary/20 data-[state=checked]:border-primary data-[state=checked]:text-primary";

// Value + ws-react copy button (used for IDs in the detail dialog).
function CopyValue({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <span className="inline-flex items-center gap-1.5 justify-end">
      <span className="font-mono break-all">{value}</span>
      <button
        type="button"
        onClick={() => { navigator.clipboard?.writeText(value); setCopied(true); window.setTimeout(() => setCopied(false), 1200); }}
        title="Copy"
        aria-label="Copy"
        className="shrink-0 p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-primary/[0.025] transition-colors"
      >
        {copied ? <Check className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
      </button>
    </span>
  );
}

function Pill({ children }: { children: React.ReactNode }) {
  return <span className="inline-block rounded-full bg-indigo-500/10 border border-indigo-500/30 text-[11px] text-indigo-300 px-2 py-0.5 m-0.5">{children}</span>;
}
function Muted() {
  return <span className="text-muted-foreground/60">—</span>;
}
function trunc(s: string, n: number) {
  s = s || "";
  return s.length > n ? s.slice(0, n) + "…" : s;
}
function parseCats(s: string): string[] {
  const seen = new Set<string>();
  return (s || "").split(",").map((x) => x.trim()).filter((x) => x && !seen.has(x) && (seen.add(x), true));
}
function Loading({ text = "Loading…" }: { text?: string }) {
  return (
    <div className="ag-loading">
      <span className="ag-spinner animate-spin" aria-hidden="true" />
      <span>{text}</span>
    </div>
  );
}
function EmptyBox({ children }: { children: React.ReactNode }) {
  return <div className="ag-empty min-h-[140px] rounded-xl border border-dashed border-border">{children}</div>;
}
function ToolBtn({ onClick, children, primary, title, disabled }: { onClick: () => void; children: React.ReactNode; primary?: boolean; title?: string; disabled?: boolean }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      disabled={disabled}
      className={cn(
        "ag-button ag-compact inline-flex items-center disabled:opacity-40 disabled:cursor-not-allowed",
        primary && "ag-primary-button"
      )}
    >
      {children}
    </button>
  );
}

function StatCard({ icon, label, value, colorClass }: { icon: React.ReactNode; label: string; value: number; colorClass: string }) {
  return (
    <div className="ag-stat">
      <span className={cn("ag-stat-icon", colorClass)} aria-hidden="true">{icon}</span>
      <div className="ag-stat-copy">
        <span className="ag-stat-value">{value}</span>
        <span className="ag-stat-label">{label}</span>
      </div>
    </div>
  );
}

// ═══════════════════════════ Classifications ═══════════════════════════

function FacetSelect({ label, options, selected, onToggle }: { label: string; options: string[]; selected: string[]; onToggle: (v: string) => void }) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button type="button" className={cn(inputCls, "flex-1 min-w-[140px] text-left flex items-center justify-between gap-2 cursor-pointer")}>
          <span>{selected.length ? `${label} (${selected.length})` : label}</span>
          <ChevronDown className="w-3.5 h-3.5 opacity-60" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-56 max-h-64 overflow-auto p-1 bg-card border-border" align="start">
        {options.length === 0 ? (
          <div className="px-3 py-2 text-xs text-muted-foreground">No values</div>
        ) : (
          options.map((o) => (
            <label key={o} className="flex items-center gap-2 px-2.5 py-1.5 rounded-md hover:bg-primary/[0.025] cursor-pointer text-sm text-foreground/85">
              <Checkbox checked={selected.includes(o)} onCheckedChange={() => onToggle(o)} className={CHECKBOX_CLS} />
              {label === "Sensitivity" ? sensitivityLabel(o) : o}
            </label>
          ))
        )}
      </PopoverContent>
    </Popover>
  );
}

function ClassificationsTab({ initial }: { initial: { correlationId?: string; traceId?: string } }) {
  const { toast } = useToast();
  const [correlationId, setCorrelationId] = useState(initial.correlationId || "");
  const [traceId, setTraceId] = useState(initial.traceId || "");
  const [facetSel, setFacetSel] = useState<Record<string, string[]>>({});
  const [options, setOptions] = useState<Record<string, string[]>>({});
  const [summary, setSummary] = useState<any>(null);
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [checked, setChecked] = useState<Set<string>>(new Set());
  const [detail, setDetail] = useState<any>(null);
  const [filterOpen, setFilterOpen] = useState(Boolean(initial.correlationId || initial.traceId));
  const [confirmState, setConfirmState] = useState<null | { title: string; message: React.ReactNode; confirmLabel: string; tone: "danger" | "default"; onConfirm: () => void }>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const params: Record<string, any> = { limit: 200 };
    if (correlationId) params.correlationId = correlationId;
    if (traceId) params.traceId = traceId;
    FACETS.forEach((f) => {
      const sel = facetSel[f.id];
      if (sel && sel.length) params[f.id] = sel;
    });
    try {
      const [sum, list] = await Promise.all([
        GatewayPostProcessorApi.classificationSummary().catch(() => null),
        GatewayPostProcessorApi.classifications(params),
      ]);
      setSummary(sum);
      setRows(Array.isArray(list) ? list : []);
      setChecked(new Set());
    } catch (e: any) {
      setError(apiError(e));
    } finally {
      setLoading(false);
    }
  }, [correlationId, traceId, facetSel]);

  useEffect(() => {
    GatewayPostProcessorApi.classificationFilters().then((o) => setOptions(o || {})).catch(() => setOptions({}));
  }, []);
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleFacet = (id: string, v: string) =>
    setFacetSel((prev) => {
      const cur = prev[id] || [];
      const next = cur.includes(v) ? cur.filter((x) => x !== v) : [...cur, v];
      return { ...prev, [id]: next };
    });

  const clearAll = () => {
    setCorrelationId("");
    setTraceId("");
    setFacetSel({});
    setTimeout(load, 0);
  };

  const toggleAll = (on: boolean) => {
    setChecked(on ? new Set(rows.map((r) => r.correlationId).filter(Boolean)) : new Set());
  };
  const toggleOne = (corr: string) =>
    setChecked((prev) => {
      const next = new Set(prev);
      next.has(corr) ? next.delete(corr) : next.add(corr);
      return next;
    });

  const reprocessOne = async (corr: string) => {
    try {
      await GatewayPostProcessorApi.reprocess(corr);
      setDetail(null);
      toast({ title: "Reprocessed" });
      load();
    } catch (e: any) {
      toast({ title: "Reprocess failed", description: apiError(e), variant: "destructive" });
    }
  };
  const reprocessSelected = async () => {
    const ids = Array.from(checked);
    if (!ids.length) {
      toast({ title: "Select at least one row first" });
      return;
    }
    try {
      const res = await GatewayPostProcessorApi.reprocessBatch(ids);
      toast({ title: `Reprocessed ${res?.updated || 0}, skipped ${res?.skipped || 0}` });
      load();
    } catch (e: any) {
      toast({ title: "Reprocess failed", description: apiError(e), variant: "destructive" });
    }
  };
  const reprocessAll = async () => {
    if (!rows.length) return;
    try {
      const res = await GatewayPostProcessorApi.reprocessAll();
      toast({ title: `Reprocessed ${res?.updated || 0}, skipped ${res?.skipped || 0}` });
      load();
    } catch (e: any) {
      toast({ title: "Reprocess failed", description: apiError(e), variant: "destructive" });
    }
  };

  const by = summary?.bySensitivity || {};
  const summaryCards: { label: string; value: number; color: string; icon: React.ReactNode }[] = summary
    ? [
        { label: "Classified responses", value: summary.total || 0, color: "text-primary", icon: <FileText className="w-4 h-4" /> },
        { label: "High", value: by.RESTRICTED || 0, color: "text-red-600 dark:text-red-400", icon: <AlertTriangle className="w-4 h-4" /> },
        { label: "Medium", value: by.CONFIDENTIAL || 0, color: "text-amber-600 dark:text-amber-400", icon: <Shield className="w-4 h-4" /> },
        { label: "Low", value: by.INTERNAL || 0, color: "text-blue-600 dark:text-blue-400", icon: <ShieldCheck className="w-4 h-4" /> },
        { label: "None", value: by.PUBLIC || 0, color: "text-emerald-600 dark:text-emerald-400", icon: <CheckCircle2 className="w-4 h-4" /> },
        { label: "Injection flags", value: summary.injectionCount || 0, color: "text-red-600 dark:text-red-400", icon: <Flag className="w-4 h-4" /> },
      ]
    : [];

  const allChecked = rows.length > 0 && rows.every((r) => !r.correlationId || checked.has(r.correlationId));

  const activeFilterCount =
    FACETS.reduce((n, f) => n + ((facetSel[f.id] || []).length > 0 ? 1 : 0), 0) +
    (correlationId.trim() ? 1 : 0) +
    (traceId.trim() ? 1 : 0);

  return (
    <div className="space-y-4">
      {summaryCards.length > 0 && (
        <div className="ag-stat-grid ag-stat-grid-compact">
          {summaryCards.map((c, i) => (
            <StatCard key={i} icon={c.icon} label={c.label} value={c.value} colorClass={c.color} />
          ))}
        </div>
      )}

      {/* filter section — collapsible panel, styled like the Audit log's GatewayFilterBar */}
      <div
        className={cn(
          "rounded-2xl border bg-card overflow-hidden transition-colors",
          filterOpen || activeFilterCount > 0
            ? "border-primary/40 shadow-[0_0_0_1px_hsl(var(--primary)/0.15)]"
            : "border-transparent portal-card-shadow"
        )}
      >
        <button
          onClick={() => setFilterOpen((v) => !v)}
          className="flex items-center justify-between w-full px-4 py-3 text-sm text-foreground hover:bg-primary/5 transition-colors group"
        >
          <div className="flex items-center gap-2">
            <div
              className={cn(
                "w-7 h-7 rounded-lg flex items-center justify-center transition-colors",
                filterOpen || activeFilterCount > 0
                  ? "bg-primary/15 text-primary"
                  : "ag-surface text-muted-foreground group-hover:bg-primary/10 group-hover:text-primary"
              )}
            >
              <Search className="w-3.5 h-3.5" />
            </div>
            <span className="font-semibold">Filters</span>
            {activeFilterCount > 0 && (
              <span className="px-2 py-0.5 rounded-full bg-primary/15 border border-primary/30 text-primary text-[10px] font-bold uppercase tracking-wider">
                {activeFilterCount} active
              </span>
            )}
            {activeFilterCount === 0 && (
              <span className="text-[11px] text-muted-foreground/70 ml-1">Click to refine results</span>
            )}
          </div>
          {filterOpen ? (
            <ChevronDown className="w-4 h-4 text-primary" />
          ) : (
            <ChevronRight className="w-4 h-4 text-muted-foreground" />
          )}
        </button>
        {filterOpen && (
          <div className="px-4 pb-4 pt-3 space-y-2.5 border-t border-border">
            <div className="flex flex-wrap items-center gap-2">
              {FACETS.map((f) => (
                <FacetSelect key={f.id} label={f.label} options={options[f.key] || []} selected={facetSel[f.id] || []} onToggle={(v) => toggleFacet(f.id, v)} />
              ))}
              <input className={cn(inputCls, "flex-1 min-w-[150px]")} placeholder="Correlation ID" value={correlationId} onChange={(e) => setCorrelationId(e.target.value)} />
              <input className={cn(inputCls, "flex-1 min-w-[150px]")} placeholder="Trace ID" value={traceId} onChange={(e) => setTraceId(e.target.value)} />
            </div>
            <div className="flex items-center justify-end gap-2">
              <ToolBtn onClick={load} primary>Apply</ToolBtn>
              <ToolBtn onClick={clearAll}>Clear</ToolBtn>
            </div>
          </div>
        )}
      </div>

      {/* table actions — right side, above the response section */}
      <div className="flex items-center justify-end gap-2 flex-wrap">
        <ToolBtn onClick={load}>Refresh</ToolBtn>
        <ToolBtn onClick={reprocessSelected} disabled={checked.size === 0} title="Re-run the classifier on the checked rows using the current rules">Reprocess selected</ToolBtn>
        <ToolBtn
          onClick={() => setConfirmState({
            title: "Reprocess all responses",
            message: <>Re-run the classifier on <b className="text-foreground">every</b> classified response using the current rules? This updates existing classifications and may take a while.</>,
            confirmLabel: "Reprocess all",
            tone: "default",
            onConfirm: reprocessAll,
          })}
          disabled={rows.length === 0}
          title={rows.length === 0 ? "Nothing to reprocess" : "Re-run the classifier on all rows using the current rules"}
        >
          Reprocess all
        </ToolBtn>
        <ToolBtn onClick={() => setChecked(new Set())} disabled={checked.size === 0} title="Deselect all checked rows">Clear selection</ToolBtn>
      </div>

      {loading ? (
        <Loading />
      ) : error ? (
        <EmptyBox>Could not load classifications — {error}</EmptyBox>
      ) : rows.length === 0 ? (
        <EmptyBox>No classifications yet. Run a governed request (e.g. a console prompt), then Refresh.</EmptyBox>
      ) : (
        <div className="rounded-xl border border-border overflow-x-auto">
          <Table className="[&_td]:px-3 [&_td]:py-2.5 [&_th]:px-3">
            <TableHeader>
              <TableRow className="border-border hover:bg-transparent">
                <TableHead className="w-10 text-center">
                  <Checkbox checked={allChecked} onCheckedChange={(v) => toggleAll(!!v)} aria-label="Select all" className={CHECKBOX_CLS} />
                </TableHead>
                <TableHead className="text-[13px] font-semibold tracking-wide text-muted-foreground">Time</TableHead>
                <TableHead className="text-[13px] font-semibold tracking-wide text-muted-foreground">Protocol</TableHead>
                <TableHead className="text-[13px] font-semibold tracking-wide text-muted-foreground">Type</TableHead>
                <TableHead className="text-[13px] font-semibold tracking-wide text-muted-foreground">Capability</TableHead>
                <TableHead className="text-[13px] font-semibold tracking-wide text-muted-foreground">Producer → Consumer</TableHead>
                <TableHead className="text-[13px] font-semibold tracking-wide text-muted-foreground">Categories</TableHead>
                <TableHead className="text-[13px] font-semibold tracking-wide text-muted-foreground">Sensitivity</TableHead>
                <TableHead className="text-[13px] font-semibold tracking-wide text-muted-foreground min-w-[210px]">Detectors</TableHead>
                <TableHead className="text-[13px] font-semibold tracking-wide text-muted-foreground">Injection</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r, i) => {
                const corr = r.correlationId || "";
                const det = r.detectors && typeof r.detectors === "object" ? Object.keys(r.detectors).join(", ") : "";
                return (
                  <TableRow key={i} className="border-border hover:bg-primary/[0.025] cursor-pointer" onClick={() => setDetail(r)}>
                    <TableCell className="w-10 text-center" onClick={(e) => e.stopPropagation()}>
                      <Checkbox checked={corr ? checked.has(corr) : false} disabled={!corr} onCheckedChange={() => corr && toggleOne(corr)} aria-label="Select row" className={CHECKBOX_CLS} />
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground whitespace-nowrap">{formatTimestamp(r.classifiedAt)}</TableCell>
                    <TableCell>{r.protocol ? <GatewayProtocolBadge protocol={r.protocol} /> : <Muted />}</TableCell>
                    <TableCell>{r.capabilityType ? <GatewayCapabilityTypeBadge type={r.capabilityType} /> : <Muted />}</TableCell>
                    <TableCell className="text-sm text-foreground/85">{r.capabilityName || "—"}</TableCell>
                    <TableCell className="text-sm text-muted-foreground whitespace-nowrap">{r.producer || "?"} <span className="text-muted-foreground/60">→</span> {r.consumer || "?"}</TableCell>
                    <TableCell>{(r.dataCategories || []).length ? <div className="flex flex-wrap">{(r.dataCategories || []).map((c: string) => <Pill key={c}>{c}</Pill>)}</div> : <Muted />}</TableCell>
                    <TableCell><GatewaySensitivityBadge value={r.sensitivity} showUnknown /></TableCell>
                    <TableCell className="min-w-[210px]">{det ? <code className="text-xs font-mono text-foreground/80 break-words">{det}</code> : <Muted />}</TableCell>
                    <TableCell>{r.injectionDetected ? <span className="text-red-600 dark:text-red-400 font-semibold text-sm inline-flex items-center gap-1"><Flag className="w-3.5 h-3.5" /> flagged</span> : <Muted />}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}

      {/* row detail */}
      <Dialog open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
        <DialogContent className="management-dialog ag-dialog sm:max-w-2xl">
          <DialogHeader className="management-dialog-header">
            <div className="management-dialog-heading">
              <span className="management-dialog-icon" aria-hidden="true">
                <FileText />
              </span>
              <div>
                <DialogTitle>Processed response</DialogTitle>
              </div>
            </div>
          </DialogHeader>
          <div className="management-dialog-body">
            {detail && (
              <div className="space-y-1">
                {[
                  ["Sensitivity", <GatewaySensitivityBadge value={detail.sensitivity} showUnknown />],
                  ["Categories", (detail.dataCategories || []).length ? (detail.dataCategories || []).map((c: string) => <Pill key={c}>{c}</Pill>) : "—"],
                  ["Capability", `${detail.capabilityType || ""} · ${detail.capabilityName || "—"}`],
                  ["Producer → Consumer", `${detail.producer || "?"} → ${detail.consumer || "?"}`],
                  ["Protocol", detail.protocol || "—"],
                  ["Injection", detail.injectionDetected ? "flagged" : "none"],
                  ["Volume", detail.volumeBytes != null ? `${detail.volumeBytes} bytes` : "—"],
                  ["Classifier", detail.classifierVersion || "—"],
                  ["When", formatTimestamp(detail.classifiedAt)],
                  ["Correlation", detail.correlationId ? <CopyValue value={detail.correlationId} /> : "—"],
                  ["Trace", detail.traceId ? <CopyValue value={detail.traceId} /> : "—"],
                ].map(([k, v], i) => (
                  <div key={i} className="flex items-center justify-between gap-4 py-1.5 border-b border-border text-[13px]">
                    <span className="text-muted-foreground">{k}</span>
                    <span className="text-foreground/90 text-right break-all">{v as React.ReactNode}</span>
                  </div>
                ))}
                <div className="pt-2">
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-1">Detector evidence (metadata only — never the raw value)</div>
                  <GatewayJsonViewer data={detail.detectors || {}} collapsed={false} maxHeight="30vh" />
                </div>
                {detail.correlationId && (
                  <div className="flex justify-end pt-3">
                    <Button onClick={() => reprocessOne(detail.correlationId)} className="bg-primary hover:bg-primary/90 text-primary-foreground shadow-[0_4px_8px_-2px_hsl(var(--primary)/0.25)]">
                      Reprocess this
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
      <ConfirmDialog state={confirmState} onClose={() => setConfirmState(null)} />
    </div>
  );
}

// ═══════════════════════════ Insights ═══════════════════════════

function Distribution({ bySensitivity }: { bySensitivity: any }) {
  if (!bySensitivity || typeof bySensitivity !== "object") return <Muted />;
  const order = ["RESTRICTED", "CONFIDENTIAL", "INTERNAL", "PUBLIC"];
  const chips = order.filter((k) => bySensitivity[k]);
  if (!chips.length) return <Muted />;
  return (
    <span className="inline-flex flex-wrap gap-1">
      {chips.map((k) => (
        <span key={k} className="inline-flex items-center gap-1">
          <GatewaySensitivityBadge value={k} /> <span className="text-[11px] text-muted-foreground">{bySensitivity[k]}</span>
        </span>
      ))}
    </span>
  );
}

function InsightsTab() {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData((await GatewayPostProcessorApi.insights()) || {});
    } catch (e: any) {
      setError(apiError(e));
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => {
    load();
  }, [load]);

  const fps = data?.fingerprints || [];
  const edges = data?.sharingEdges || [];
  const drift = data?.drift || [];

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <span className="flex-1 text-xs text-muted-foreground">Learned from the classification history — which tools/agents handle sensitive data, who shares it, and what has drifted.</span>
        <ToolBtn onClick={load}>Refresh</ToolBtn>
      </div>
      {loading ? (
        <Loading />
      ) : error ? (
        <EmptyBox>Could not load insights — {error}</EmptyBox>
      ) : !fps.length && !edges.length && !drift.length ? (
        <EmptyBox>No insights yet — run some governed requests first, then Refresh.</EmptyBox>
      ) : (
        <div className="space-y-6">
          {drift.length > 0 && (
            <div>
              <h3 className="text-[12px] uppercase tracking-wider text-muted-foreground font-semibold mb-2.5">Drift alerts</h3>
              <div className="space-y-2">
                {drift.map((d: any, i: number) => (
                  <div key={i} className="flex items-center gap-2 rounded-lg border border-red-500/30 bg-red-500/5 px-3 py-2.5 text-[13px]">
                    <AlertTriangle className="w-4 h-4 text-red-600 dark:text-red-400 shrink-0" />
                    <span className="text-foreground/90">
                      <strong>{d.capabilityName || "?"}</strong> <span className="text-muted-foreground">({d.producer || "?"})</span> escalated{" "}
                      <GatewaySensitivityBadge value={d.baselinePeak} showUnknown /> <span className="text-muted-foreground/60">→</span> <GatewaySensitivityBadge value={d.recentPeak} showUnknown />
                      <span className="text-muted-foreground"> · {d.recentCount || 0} recent</span>
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {fps.length > 0 && (
            <div>
              <h3 className="text-[12px] uppercase tracking-wider text-muted-foreground font-semibold mb-2.5">Sensitive capabilities (tools &amp; agents)</h3>
              <div className="rounded-xl border border-border overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-border hover:bg-transparent">
                      {["Producer", "Capability", "Type", "Total", "Sensitive", "Peak", "Distribution", "Last seen"].map((h) => (
                        <TableHead key={h} className="text-xs text-muted-foreground">{h}</TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {fps.map((f: any, i: number) => (
                      <TableRow key={i} className="border-border hover:bg-primary/[0.025]">
                        <TableCell className="text-xs text-foreground/85">{f.producer || "?"}</TableCell>
                        <TableCell className="text-xs text-foreground/85">{f.capabilityName || "—"}</TableCell>
                        <TableCell>{f.capabilityType ? <GatewayCapabilityTypeBadge type={f.capabilityType} /> : <Muted />}</TableCell>
                        <TableCell className="text-xs text-foreground/85">{f.total || 0}</TableCell>
                        <TableCell className="text-xs">{f.sensitiveCount ? <strong className="text-foreground">{f.sensitiveCount}</strong> : <span className="text-muted-foreground">0</span>}</TableCell>
                        <TableCell><GatewaySensitivityBadge value={f.peakSensitivity} showUnknown /></TableCell>
                        <TableCell><Distribution bySensitivity={f.bySensitivity} /></TableCell>
                        <TableCell className="text-xs text-muted-foreground whitespace-nowrap">{formatTimestamp(f.lastSeen)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}
          {edges.length > 0 && (
            <div>
              <h3 className="text-[12px] uppercase tracking-wider text-muted-foreground font-semibold mb-2.5">Data sharing (who sends sensitive data to whom)</h3>
              <div className="rounded-xl border border-border overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-border hover:bg-transparent">
                      {["Producer → Consumer", "Total", "Sensitive", "Peak", "Distribution", "Last seen"].map((h) => (
                        <TableHead key={h} className="text-xs text-muted-foreground">{h}</TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {edges.map((e: any, i: number) => (
                      <TableRow key={i} className="border-border hover:bg-primary/[0.025]">
                        <TableCell className="text-xs text-foreground/85 whitespace-nowrap">{e.producer || "?"} <span className="text-muted-foreground/60">→</span> {e.consumer || "?"}</TableCell>
                        <TableCell className="text-xs text-foreground/85">{e.total || 0}</TableCell>
                        <TableCell className="text-xs">{e.sensitiveCount ? <strong className="text-foreground">{e.sensitiveCount}</strong> : <span className="text-muted-foreground">0</span>}</TableCell>
                        <TableCell><GatewaySensitivityBadge value={e.peakSensitivity} showUnknown /></TableCell>
                        <TableCell><Distribution bySensitivity={e.bySensitivity} /></TableCell>
                        <TableCell className="text-xs text-muted-foreground whitespace-nowrap">{formatTimestamp(e.lastSeen)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════ Rules ═══════════════════════════

const EMPTY_RULE = { id: "", name: "", matchType: "REGEX", pattern: "", contextKey: "", keywords: "", dataCategories: "", sensitivity: "INTERNAL", confidence: "", description: "" };

function RuleFormDialog({ open, rule, onClose, onSaved }: { open: boolean; rule: any | null; onClose: () => void; onSaved: () => void }) {
  const { toast } = useToast();
  const [form, setForm] = useState<any>(EMPTY_RULE);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setError("");
    setForm(
      rule
        ? {
            id: rule.id || "",
            name: rule.name || "",
            matchType: rule.matchType || "REGEX",
            pattern: rule.pattern || "",
            contextKey: rule.contextKey || "",
            keywords: (rule.keywords || []).join(", "),
            dataCategories: (rule.dataCategories || []).join(", "),
            sensitivity: rule.sensitivity || "INTERNAL",
            confidence: rule.confidence != null ? String(rule.confidence) : "",
            description: rule.description || "",
          }
        : { ...EMPTY_RULE }
    );
  }, [open, rule]);

  const set = (k: string, v: any) => setForm((f: any) => ({ ...f, [k]: v }));

  const save = async () => {
    const payload: any = {
      name: form.name,
      ruleType: "CUSTOM",
      matchType: form.matchType,
      description: form.description,
      dataCategories: parseCats(form.dataCategories),
      sensitivity: form.sensitivity,
    };
    if (form.matchType === "KEYWORDS") payload.keywords = parseCats(form.keywords);
    else {
      payload.pattern = form.pattern;
      payload.contextKey = form.contextKey || null;
    }
    if (form.confidence) payload.confidence = parseFloat(form.confidence);
    setSaving(true);
    try {
      if (form.id) await GatewayPostProcessorApi.updateRule(form.id, payload);
      else await GatewayPostProcessorApi.createRule(payload);
      toast({ title: "Rule saved" });
      onSaved();
      onClose();
    } catch (e: any) {
      setError(apiError(e) || "Could not save rule");
    } finally {
      setSaving(false);
    }
  };

  const lbl = "flex flex-col gap-1 text-xs text-muted-foreground";
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="management-dialog ag-dialog sm:max-w-lg">
        <DialogHeader className="management-dialog-header">
          <div className="management-dialog-heading">
            <span className="management-dialog-icon" aria-hidden="true">
              {rule ? <Pencil /> : <Plus />}
            </span>
            <div>
              <DialogTitle>{rule ? "Edit custom rule" : "Add custom rule"}</DialogTitle>
            </div>
          </div>
        </DialogHeader>
        <div className="management-dialog-body">
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground">A custom detector that runs alongside the built-ins. Match by a <b>regex pattern</b> or a plain <b>keyword list</b>.</p>
            <label className={lbl}>Name<input className={cn(inputCls, "w-full")} value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="e.g. Internal ticket IDs" /></label>
            <label className={lbl}>Match by
              <select className={cn(inputCls, "w-full")} value={form.matchType} onChange={(e) => set("matchType", e.target.value)}>
                <option value="REGEX">Regex pattern — for shapes like ACME-1234, emails, IDs</option>
                <option value="KEYWORDS">Keyword list — for specific words / names</option>
              </select>
            </label>
            {form.matchType === "REGEX" ? (
              <div className="flex flex-col gap-3 border-l-2 border-border pl-3">
                <label className={lbl}>Regex pattern<input className={cn(inputCls, "w-full font-mono")} value={form.pattern} onChange={(e) => set("pattern", e.target.value)} placeholder="ACME-\d{4}" /></label>
                <label className={lbl}>Context keyword (optional)
                  <select className={cn(inputCls, "w-full")} value={form.contextKey} onChange={(e) => set("contextKey", e.target.value)}>
                    {CONTEXT_KEYS.map((c) => <option key={c} value={c}>{c || "(none)"}</option>)}
                  </select>
                </label>
              </div>
            ) : (
              <div className="flex flex-col gap-1.5 border-l-2 border-border pl-3">
                <label className={lbl}>Keywords (comma-separated)<input className={cn(inputCls, "w-full")} value={form.keywords} onChange={(e) => set("keywords", e.target.value)} placeholder="Bluebird, Falcon, Titan" /></label>
                <span className="text-[11px] text-muted-foreground">Matched whole-word and case-insensitively — no regex needed.</span>
              </div>
            )}
            <label className={lbl}>Categories (comma-separated)<input className={cn(inputCls, "w-full")} value={form.dataCategories} onChange={(e) => set("dataCategories", e.target.value)} placeholder="PROJECT, INTERNAL_ID" /></label>
            <label className={lbl}>Sensitivity
              <select className={cn(inputCls, "w-full")} value={form.sensitivity} onChange={(e) => set("sensitivity", e.target.value)}>
                {SENS_OPTIONS.map((s) => <option key={s} value={s}>{sensitivityLabel(s)}</option>)}
              </select>
            </label>
            <label className={lbl}>Confidence (optional, 0–1)<input className={cn(inputCls, "w-full")} value={form.confidence} onChange={(e) => set("confidence", e.target.value)} placeholder="0.9" /></label>
            <label className={lbl}>Description (optional)<input className={cn(inputCls, "w-full")} value={form.description} onChange={(e) => set("description", e.target.value)} /></label>
            {error && <div className="text-xs text-red-600 dark:text-red-400">{error}</div>}
          </div>
        </div>
        <DialogFooter className="management-dialog-footer">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-primary hover:bg-primary/90 text-primary-foreground shadow-[0_4px_8px_-2px_hsl(var(--primary)/0.25)]">
            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function OverrideDialog({ matcher, detector, onClose, onSaved }: { matcher: string | null; detector: any; onClose: () => void; onSaved: () => void }) {
  const { toast } = useToast();
  const [cats, setCats] = useState("");
  const [sens, setSens] = useState("INTERNAL");
  const [error, setError] = useState("");

  useEffect(() => {
    if (!matcher || !detector) return;
    setError("");
    const cur = (detector.overrideCategories && detector.overrideCategories.length ? detector.overrideCategories : detector.defaultCategory ? [detector.defaultCategory] : []).join(", ");
    setCats(cur);
    const s = detector.overrideSensitivity || detector.defaultSensitivity;
    setSens(s && s !== "-" ? s : "INTERNAL");
  }, [matcher, detector]);

  const save = async () => {
    try {
      await GatewayPostProcessorApi.detectorOverride(matcher!, { dataCategories: parseCats(cats), sensitivity: sens });
      toast({ title: "Override saved" });
      onSaved();
      onClose();
    } catch (e: any) {
      setError(apiError(e) || "Could not save override");
    }
  };

  const lbl = "flex flex-col gap-1 text-xs text-muted-foreground";
  return (
    <Dialog open={!!matcher} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="management-dialog ag-dialog sm:max-w-md">
        <DialogHeader className="management-dialog-header">
          <div className="management-dialog-heading">
            <span className="management-dialog-icon" aria-hidden="true">
              <SlidersHorizontal />
            </span>
            <div>
              <DialogTitle>Override: {matcher}</DialogTitle>
            </div>
          </div>
        </DialogHeader>
        <div className="management-dialog-body">
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground">Remap what <code className="font-mono text-primary">{matcher}</code> tags. It keeps finding the same data — only the category/sensitivity change.</p>
            <label className={lbl}>Categories (comma-separated)<input className={cn(inputCls, "w-full")} value={cats} onChange={(e) => setCats(e.target.value)} placeholder="INFRA, NETWORK_ASSET" /></label>
            <label className={lbl}>Sensitivity
              <select className={cn(inputCls, "w-full")} value={sens} onChange={(e) => setSens(e.target.value)}>
                {SENS_OPTIONS.map((s) => <option key={s} value={s}>{sensitivityLabel(s)}</option>)}
              </select>
            </label>
            {error && <div className="text-xs text-red-600 dark:text-red-400">{error}</div>}
          </div>
        </div>
        <DialogFooter className="management-dialog-footer">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} className="bg-primary hover:bg-primary/90 text-primary-foreground shadow-[0_4px_8px_-2px_hsl(var(--primary)/0.25)]">Save override</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TemplateLibraryDialog({ open, onClose, onChanged }: { open: boolean; onClose: () => void; onChanged: () => void }) {
  const { toast } = useToast();
  const [packs, setPacks] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const p = await GatewayPostProcessorApi.templates();
      setPacks(Array.isArray(p) ? p : []);
    } catch {
      setPacks([]);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => {
    if (open) refresh();
  }, [open, refresh]);

  const installOne = async (templateId: string) => {
    try {
      await GatewayPostProcessorApi.installTemplate(templateId);
      toast({ title: "Template installed" });
      refresh();
      onChanged();
    } catch (e: any) {
      toast({ title: "Could not install template", description: apiError(e), variant: "destructive" });
    }
  };
  const installWholePack = async (industry: string) => {
    try {
      const r = (await GatewayPostProcessorApi.installPack(industry)) || {};
      const parts = [`${r.installed || 0} added`];
      if (r.alreadyInstalled) parts.push(`${r.alreadyInstalled} already installed`);
      if (r.conflicts && r.conflicts.length) parts.push(`${r.conflicts.length} skipped (name in use)`);
      toast({ title: `${industry}: ${parts.join(", ")}` });
      refresh();
      onChanged();
    } catch (e: any) {
      toast({ title: "Could not install pack", description: apiError(e), variant: "destructive" });
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="management-dialog ag-dialog sm:max-w-3xl">
        <DialogHeader className="management-dialog-header">
          <div className="management-dialog-heading">
            <span className="management-dialog-icon" aria-hidden="true">
              <BookOpen />
            </span>
            <div>
              <DialogTitle>Rule templates</DialogTitle>
            </div>
          </div>
        </DialogHeader>
        <div className="management-dialog-body">
          {loading ? (
            <Loading text="Loading templates…" />
          ) : !packs.length ? (
            <EmptyBox>No templates available.</EmptyBox>
          ) : (
            <div className="space-y-3">
              <p className="text-xs text-muted-foreground">Curated detectors for common regulations. Installing one adds a normal rule you can then edit or delete — none duplicate the built-in detectors.</p>
              {packs.map((p, idx) => {
                const total = (p.templates || []).length;
                const allInstalled = (p.installedCount || 0) >= total;
                return (
                  <div key={idx} className="rounded-xl border border-border p-3">
                    <div className="flex items-center gap-2 flex-wrap mb-2">
                      <span className="font-semibold text-foreground">{p.industry}</span>
                      {p.regulation && <Pill>{p.regulation}</Pill>}
                      <span className="text-xs text-muted-foreground">{p.installedCount || 0}/{total} installed</span>
                      <span className="flex-1" />
                      {allInstalled ? (
                        <span className="text-xs text-muted-foreground">All added</span>
                      ) : (
                        <button
                          type="button"
                          onClick={() => installWholePack(p.industry)}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary/10 text-primary border border-primary/30 text-xs font-medium hover:bg-primary/20 transition-colors"
                        >
                          <Plus className="w-3.5 h-3.5" /> Add whole pack
                        </button>
                      )}
                    </div>
                    <div className="rounded-lg border border-border overflow-x-auto">
                      <Table>
                        <TableBody>
                          {(p.templates || []).map((t: any) => (
                            <TableRow key={t.templateId} className="border-border hover:bg-primary/[0.025]">
                              <TableCell>
                                <b className="text-foreground text-sm">{t.name}</b>
                                <div className="text-[11px] text-muted-foreground mt-0.5">{t.description || ""}</div>
                              </TableCell>
                              <TableCell className="text-xs">
                                {t.matchType === "KEYWORDS" ? (
                                  <span><span className="text-muted-foreground/70">words:</span> {trunc((t.keywords || []).join(", "), 44)}</span>
                                ) : (
                                  <code className="font-mono text-foreground/80">{trunc(t.pattern || "", 42)}</code>
                                )}
                              </TableCell>
                              <TableCell>{(t.dataCategories || []).length ? (t.dataCategories || []).map((c: string) => <Pill key={c}>{c}</Pill>) : <Muted />}</TableCell>
                              <TableCell><GatewaySensitivityBadge value={t.sensitivity} showUnknown /></TableCell>
                              <TableCell className="text-right">
                                {t.installed ? (
                                  <span className="text-xs text-emerald-600 dark:text-emerald-400 inline-flex items-center gap-1 whitespace-nowrap"><Check className="w-3.5 h-3.5" /> Added</span>
                                ) : (
                                  <button
                                    type="button"
                                    onClick={() => installOne(t.templateId)}
                                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-primary/10 text-primary border border-primary/30 text-xs font-medium hover:bg-primary/20 transition-colors"
                                  >
                                    <Plus className="w-3 h-3" /> Add
                                  </button>
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

const RULE_CARD_PREFIX = "__RULE_CARD__";
const RULE_CARD_SUFFIX = "__END_RULE_CARD__";

function RuleAssistantDialog({ open, editRule, onClose, onSaved }: { open: boolean; editRule: any | null; onClose: () => void; onSaved: () => void }) {
  const { toast } = useToast();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [thinking, setThinking] = useState(false);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const editing = !!editRule;

  useEffect(() => {
    if (!open) return;
    setMessages([]);
    setSuggestions([]);
    if (!editing) {
      GatewayPostProcessorApi.ruleChatSuggestions().then((r) => setSuggestions((r && r.suggestions) || [])).catch(() => setSuggestions([]));
    }
  }, [open, editing]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const send = async (text: string) => {
    const prompt = text.trim();
    if (!prompt || thinking) return;
    const nextMsgs: ChatMessage[] = [...messages, { role: "user", content: prompt }];
    setMessages(nextMsgs);
    setSuggestions([]);
    setThinking(true);
    try {
      // Draft cards are encoded into assistant message content; strip them back out of the history we replay to the model.
      const msgs = nextMsgs.map((m) => ({ role: m.role, content: m.content.startsWith(RULE_CARD_PREFIX) ? "" : m.content }));
      const req: any = msgs.length > 1 ? { messages: msgs } : { prompt };
      if (editRule) req.currentRule = editRule;
      const resp = await GatewayPostProcessorApi.ruleChat(req);
      if (!resp || resp.success === false) {
        setMessages(messages);
        toast({ title: (resp && resp.error) || "The assistant failed. Try again.", variant: "destructive" });
      } else if (!resp.conversationComplete && resp.followUpQuestion) {
        setMessages([...nextMsgs, { role: "assistant", content: resp.followUpQuestion }]);
      } else {
        setMessages([...nextMsgs, { role: "assistant", content: `${RULE_CARD_PREFIX}${JSON.stringify(resp)}${RULE_CARD_SUFFIX}` }]);
      }
    } catch (e: any) {
      setMessages(messages);
      toast({ title: apiError(e) || "The assistant failed. Try again.", variant: "destructive" });
    } finally {
      setThinking(false);
    }
  };

  const applyDraft = async (d: any) => {
    if (!d) return;
    const payload: any = {
      name: d.suggestedName || "AI rule",
      ruleType: "CUSTOM",
      matchType: d.matchType || "REGEX",
      dataCategories: d.dataCategories || [],
      sensitivity: d.sensitivity,
      description: d.explanation || null,
    };
    if ((d.matchType || "REGEX") === "KEYWORDS") payload.keywords = d.keywords || [];
    else {
      payload.pattern = d.pattern;
      if (d.contextKey) payload.contextKey = d.contextKey;
    }
    if (d.confidence != null) payload.confidence = d.confidence;
    try {
      if (editRule && editRule.id) {
        await GatewayPostProcessorApi.updateRule(editRule.id, payload);
        toast({ title: "Rule updated" });
      } else {
        await GatewayPostProcessorApi.createRule(payload);
        toast({ title: "Rule created" });
      }
      onSaved();
      onClose();
    } catch (e: any) {
      toast({ title: "Could not save rule", description: apiError(e), variant: "destructive" });
    }
  };

  const DraftCard = ({ d }: { d: any }) => (
    <div className="rounded-lg border border-border ag-surface p-3 mt-1">
      <div className="flex items-center gap-2 text-sm font-semibold text-foreground mb-1.5"><Wand2 className="w-4 h-4 text-primary" /> {d.suggestedName || "New rule"}</div>
      {d.explanation && <div className="text-xs text-muted-foreground mb-2">{d.explanation}</div>}
      <div className="text-[13px] text-foreground/90 mb-2">
        {d.matchType === "KEYWORDS" ? (
          <>Flags these words: <b>{(d.keywords || []).join(", ")}</b> <span className="text-muted-foreground/70">(whole-word, case-insensitive)</span></>
        ) : (
          <>Flags text matching <code className="font-mono text-primary">{d.pattern || ""}</code></>
        )}
      </div>
      <div className="flex items-center gap-1.5 flex-wrap text-xs">
        <span className="text-muted-foreground">Tags as</span>
        {(d.dataCategories || []).length ? (d.dataCategories || []).map((c: string) => <Pill key={c}>{c}</Pill>) : <Muted />}
        <span className="text-muted-foreground ml-1">· Sensitivity</span> <b className="text-foreground">{sensitivityLabel(d.sensitivity)}</b>
      </div>
      {d.dataNote && <div className="mt-2 text-xs text-amber-600 dark:text-amber-400">💡 {d.dataNote}</div>}
      {d.validationError && <div className="mt-2 text-xs text-amber-600 dark:text-amber-400">⚠ {d.validationError} — tell me how to fix it.</div>}
      <div className="mt-3">
        <Button disabled={!!d.validationError} onClick={() => applyDraft(d)} className="bg-primary hover:bg-primary/90 text-primary-foreground shadow-[0_4px_8px_-2px_hsl(var(--primary)/0.25)]">
          {editing ? "Update This Rule" : "Create This Rule"}
        </Button>
      </div>
    </div>
  );

  // GatewayChatPanel only hands the renderer the message content string, so the draft rides along encoded in it.
  const renderRuleCard = (content: string): React.ReactNode | null => {
    const match = content.match(/^__RULE_CARD__([\s\S]+)__END_RULE_CARD__$/);
    if (!match) return null;
    try {
      return <DraftCard d={JSON.parse(match[1])} />;
    } catch {
      return null;
    }
  };

  // Edit mode captures the rule's current state (mirrors the OG ruleEditWelcome) so the user sees exactly what they're changing.
  const editCats: string[] = editRule?.dataCategories || [];
  const editIsKeywords = editRule?.matchType === "KEYWORDS";
  const welcomeMessage: React.ReactNode = editing ? (
    <div className="text-left">
      <p className="text-center mb-2.5">
        Editing <b className="text-foreground">“{editRule?.name || "rule"}”</b> — here's where it stands:
      </p>
      <div className="rounded-lg border border-border ag-surface p-3 space-y-2">
        <div className="flex gap-2">
          <span className="text-muted-foreground/60 w-[74px] shrink-0">{editIsKeywords ? "Keywords" : "Pattern"}</span>
          <code className="font-mono text-primary break-all">{editIsKeywords ? (editRule?.keywords || []).join(", ") || "—" : editRule?.pattern || "—"}</code>
        </div>
        <div className="flex gap-2 items-start">
          <span className="text-muted-foreground/60 w-[74px] shrink-0 pt-0.5">Tags</span>
          <span className="flex flex-wrap">{editCats.length ? editCats.map((c) => <Pill key={c}>{c}</Pill>) : <span className="text-muted-foreground">—</span>}</span>
        </div>
        <div className="flex gap-2 items-center">
          <span className="text-muted-foreground/60 w-[74px] shrink-0">Sensitivity</span>
          <GatewaySensitivityBadge value={editRule?.sensitivity} showUnknown />
        </div>
      </div>
      <p className="text-center mt-2.5">
        Tell me what to change — e.g. “also flag ACME-5678”, “make it high”, or “add the word Falcon”.
      </p>
    </div>
  ) : (
    "Describe the data you want flagged in plain English. I'll draft a rule for you to review and create — and I'll ask if I need more. No regex required."
  );

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="w-full max-w-2xl rounded-[18px] border border-border/60 bg-card shadow-[0_24px_80px_rgb(15_23_42/0.18)] flex flex-col h-[75vh] overflow-hidden">
        <div className="flex items-center justify-between px-4 py-4 border-b border-border">
          <div className="flex items-center gap-2">
            <Wand2 className="w-4 h-4 text-primary" />
            <span className="text-sm font-semibold text-foreground">{editing ? `Edit "${editRule?.name || "rule"}" with AI` : "Create rule with AI"}</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
              <Brain className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400" /> <span className="text-emerald-600 dark:text-emerald-400">AI Active</span>
            </span>
            <button className="text-muted-foreground hover:text-foreground transition-colors" onClick={onClose}>
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
        <div className="flex-1 flex flex-col min-h-0">
          <GatewayChatPanel
            messages={messages}
            onSend={send}
            loading={thinking}
            suggestions={editing ? undefined : suggestions}
            placeholder={editing ? "Describe the change…" : "Describe the rule you want…"}
            welcomeMessage={welcomeMessage}
            className="flex-1"
            customRenderer={renderRuleCard}
          />
        </div>
      </div>
    </div>
  );
}

function RuleTypeBadge({ label, cls }: { label: string; cls: string }) {
  return <span className={cn("ml-1.5 align-middle inline-block whitespace-nowrap text-[10px] px-1.5 py-px rounded border", cls)}>{label}</span>;
}

// ws-react themed confirmation (replaces native window.confirm).
function ConfirmDialog({ state, onClose }: { state: null | { title: string; message: React.ReactNode; confirmLabel: string; tone: "danger" | "default"; onConfirm: () => void }; onClose: () => void }) {
  return (
    <Dialog open={!!state} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="management-dialog ag-dialog ag-confirm">
        <DialogHeader className="management-dialog-header">
          <div className="management-dialog-heading">
            <span className="management-dialog-icon" data-tone={state?.tone === "default" ? undefined : "danger"} aria-hidden="true">
              {state?.tone === "default" ? <Check /> : <AlertTriangle />}
            </span>
            <div>
              <DialogTitle>{state?.title}</DialogTitle>
              <DialogDescription asChild>
                <div>{state?.message}</div>
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>
        <DialogFooter className="management-dialog-footer">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            onClick={() => { state?.onConfirm(); onClose(); }}
            className={state?.tone === "default" ? "endpoint-primary-action" : "ag-danger-button"}
          >
            {state?.confirmLabel || "Confirm"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RulesTab() {
  const { toast } = useToast();
  const [rules, setRules] = useState<any[]>([]);
  const [detectors, setDetectors] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<"custom" | "builtin">("custom");
  const [aiAvailable, setAiAvailable] = useState(false);
  const [formRule, setFormRule] = useState<{ open: boolean; rule: any | null }>({ open: false, rule: null });
  const [assistant, setAssistant] = useState<{ open: boolean; rule: any | null }>({ open: false, rule: null });
  const [templatesOpen, setTemplatesOpen] = useState(false);
  const [override, setOverride] = useState<{ matcher: string | null; detector: any }>({ matcher: null, detector: null });

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [r, eff] = await Promise.all([
        GatewayPostProcessorApi.rules(),
        GatewayPostProcessorApi.detectorsEffective().catch(() => []),
      ]);
      setRules((Array.isArray(r) ? r : []).filter((x: any) => x.ruleType === "CUSTOM"));
      setDetectors(Array.isArray(eff) ? eff : []);
    } catch (e: any) {
      setError(apiError(e));
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => {
    load();
    GatewayPostProcessorApi.ruleChatStatus().then((s) => setAiAvailable(!!(s && s.llmAvailable))).catch(() => setAiAvailable(false));
  }, [load]);

  const [confirmState, setConfirmState] = useState<null | { title: string; message: React.ReactNode; confirmLabel: string; tone: "danger" | "default"; onConfirm: () => void }>(null);

  const toggleRule = async (r: any) => {
    try { await GatewayPostProcessorApi.toggleRule(r.id); load(); } catch (e: any) { toast({ title: "Toggle failed", description: apiError(e), variant: "destructive" }); }
  };
  const deleteRule = (r: any) => {
    setConfirmState({
      title: "Delete rule",
      message: <>Delete the rule <span className="font-semibold text-foreground">“{r.name}”</span>? This can’t be undone.</>,
      confirmLabel: "Delete",
      tone: "danger",
      onConfirm: async () => {
        try { await GatewayPostProcessorApi.deleteRule(r.id); toast({ title: "Rule deleted" }); load(); } catch (e: any) { toast({ title: "Delete failed", description: apiError(e), variant: "destructive" }); }
      },
    });
  };
  const toggleDetector = async (matcher: string, enabled: boolean) => {
    try { await GatewayPostProcessorApi.detectorEnabled(matcher, enabled); load(); } catch (e: any) { toast({ title: "Could not update detector", description: apiError(e), variant: "destructive" }); }
  };
  const clearOverride = (matcher: string) => {
    setConfirmState({
      title: "Clear override",
      message: <>Clear the override on <span className="font-semibold text-foreground">“{matcher}”</span> and revert it to the default mapping?</>,
      confirmLabel: "Clear override",
      tone: "danger",
      onConfirm: async () => {
        try { await GatewayPostProcessorApi.detectorClearOverride(matcher); toast({ title: "Override cleared" }); load(); } catch (e: any) { toast({ title: "Could not clear override", description: apiError(e), variant: "destructive" }); }
      },
    });
  };

  const Toggle = ({ on, onClick }: { on: boolean; onClick: () => void }) => (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-label={on ? "Disable" : "Enable"}
      onClick={onClick}
      className={cn(
        "relative inline-flex h-4 w-7 shrink-0 items-center rounded-full transition-colors",
        on ? "bg-emerald-500" : "bg-muted-foreground/30"
      )}
    >
      <span className={cn("inline-block h-3 w-3 rounded-full bg-white shadow transition-transform", on ? "translate-x-3.5" : "translate-x-0.5")} />
    </button>
  );
  // ws-react row-action convention: ghost icon button (muted → tinted on hover).
  const IconBtn = ({ onClick, title, tone, children }: { onClick: () => void; title: string; tone?: "primary" | "danger"; children: React.ReactNode }) => (
    <button
      type="button"
      onClick={onClick}
      title={title}
      aria-label={title}
      className={cn(
        "p-1.5 rounded-md text-muted-foreground transition-colors",
        tone === "danger" ? "hover:text-red-400 hover:bg-red-500/10" : tone === "primary" ? "hover:text-primary hover:bg-primary/10" : "hover:text-foreground hover:bg-primary/[0.025]"
      )}
    >
      {children}
    </button>
  );

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => setFormRule({ open: true, rule: null })}
          className="flex items-center gap-2 px-4 py-2 rounded-lg border border-primary bg-primary text-white text-sm font-medium hover:bg-primary/90 transition-colors"
        >
          <Plus className="w-4 h-4" /> Add rule
        </button>
        <button
          type="button"
          onClick={() => setTemplatesOpen(true)}
          className="flex items-center gap-2 px-4 py-2 rounded-lg border border-border ag-surface text-muted-foreground text-sm font-medium hover:text-foreground hover:border-primary/40 transition-colors"
        >
          <BookOpen className="w-4 h-4" /> Rule templates
        </button>
        {aiAvailable && (
          <button
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-gradient-to-r from-primary/15 to-violet-500/15 border border-primary/30 text-primary hover:from-primary/25 hover:to-violet-500/25 hover:border-primary/50 text-sm font-medium transition-all group"
            onClick={() => setAssistant({ open: true, rule: null })}
          >
            <Wand2 className="w-4 h-4 group-hover:rotate-12 transition-transform duration-300" />
            Create with AI
            <span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse" />
          </button>
        )}
        {!aiAvailable && (
          <span className="text-xs text-muted-foreground">Add a custom rule, or manage the built-ins, in the tabs below.</span>
        )}
      </div>

      {loading ? (
        <Loading />
      ) : error ? (
        <EmptyBox>Could not load rules — {error}</EmptyBox>
      ) : (
        <>
          <div className="flex items-center gap-1 border-b border-border">
            {[
              { id: "custom" as const, label: `Custom rules (${rules.length})` },
              { id: "builtin" as const, label: `Built-in detectors (${detectors.length})` },
            ].map((t) => (
              <button key={t.id} onClick={() => setTab(t.id)} className={cn("px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors", tab === t.id ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground")}>
                {t.label}
              </button>
            ))}
          </div>

          {tab === "custom" ? (
            <>
              <div className="flex items-center justify-between gap-3">
                <p className="text-xs text-muted-foreground">Your own regex detectors, layered on the built-ins.</p>
                <ToolBtn onClick={load}>Refresh</ToolBtn>
              </div>
              <div className="rounded-xl border border-border overflow-x-auto">
                <Table className="table-fixed">
                  <TableHeader>
                    <TableRow className="border-border hover:bg-transparent">
                      {[
                        { h: "Name", w: "w-[22%]" },
                        { h: "Match", w: "w-[24%]" },
                        { h: "Category", w: "w-[22%]" },
                        { h: "Sensitivity", w: "w-[12%]" },
                        { h: "Active", w: "w-[8%]" },
                        { h: "Actions", w: "w-[12%] text-right" },
                      ].map(({ h, w }, i) => <TableHead key={i} className={cn("text-xs text-muted-foreground", w)}>{h}</TableHead>)}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {rules.length === 0 ? (
                      <TableRow><TableCell colSpan={6} className="text-sm text-muted-foreground py-4">No custom rules yet — “Add rule” creates a regex detector of your own.</TableCell></TableRow>
                    ) : (
                      rules.map((r, i) => (
                        <TableRow key={i} className={cn("border-border hover:bg-primary/[0.025]", !r.enabled && "opacity-55")}>
                          <TableCell className="text-sm text-foreground leading-snug align-top">
                            {r.name || "—"}
                            {r.sourceTemplateId && <RuleTypeBadge label={r.sourceTemplateIndustry ? `${r.sourceTemplateIndustry} template` : "Template"} cls="border-violet-500/50 text-violet-600 dark:text-violet-400" />}
                          </TableCell>
                          <TableCell className="text-xs">
                            {(r.matchType || "REGEX") === "KEYWORDS" ? (
                              <><span className="font-mono text-foreground/80" title="keyword list">{trunc((r.keywords || []).join(", "), 36)}</span> <span className="text-[10px] text-muted-foreground/60">keywords</span></>
                            ) : (
                              <span className="font-mono text-foreground/80">{trunc(r.pattern || "", 40)}</span>
                            )}
                          </TableCell>
                          <TableCell>{(r.dataCategories || []).length ? <div className="flex flex-wrap">{(r.dataCategories || []).map((c: string) => <Pill key={c}>{c}</Pill>)}</div> : <Muted />}</TableCell>
                          <TableCell>{r.sensitivity ? <GatewaySensitivityBadge value={r.sensitivity} showUnknown /> : <Muted />}</TableCell>
                          <TableCell><Toggle on={!!r.enabled} onClick={() => toggleRule(r)} /></TableCell>
                          <TableCell className="text-right whitespace-nowrap">
                            <div className="inline-flex items-center justify-end gap-1">
                              <IconBtn title="Edit" onClick={() => setFormRule({ open: true, rule: r })}><Pencil className="w-3.5 h-3.5" /></IconBtn>
                              {aiAvailable && <IconBtn title="Edit with AI" tone="primary" onClick={() => setAssistant({ open: true, rule: r })}><Wand2 className="w-3.5 h-3.5" /></IconBtn>}
                              <IconBtn title="Delete" tone="danger" onClick={() => deleteRule(r)}><Trash2 className="w-3.5 h-3.5" /></IconBtn>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </>
          ) : (
            <>
              <div className="flex items-center justify-between gap-3">
                <p className="text-xs text-muted-foreground">Always active by default — disable or remap them, but they can’t be deleted.</p>
                <ToolBtn onClick={load}>Refresh</ToolBtn>
              </div>
              <div className="rounded-xl border border-border overflow-x-auto">
                <Table className="table-fixed">
                  <TableHeader>
                    <TableRow className="border-border hover:bg-transparent">
                      {[
                        { h: "Detector", w: "w-[20%]" },
                        { h: "What it finds", w: "w-[24%]" },
                        { h: "Category", w: "w-[20%]" },
                        { h: "Sensitivity", w: "w-[12%]" },
                        { h: "Active", w: "w-[8%]" },
                        { h: "Actions", w: "w-[16%] text-right" },
                      ].map(({ h, w }, i) => <TableHead key={i} className={cn("text-xs text-muted-foreground", w)}>{h}</TableHead>)}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {detectors.map((d, i) => {
                      const overridden = !!((d.overrideCategories && d.overrideCategories.length) || d.overrideSensitivity);
                      const cats = overridden && d.overrideCategories && d.overrideCategories.length ? d.overrideCategories : d.defaultCategory ? [d.defaultCategory] : [];
                      const sensVal = overridden && d.overrideSensitivity ? d.overrideSensitivity : d.defaultSensitivity;
                      return (
                        <TableRow key={i} className={cn("border-border hover:bg-primary/[0.025]", !d.enabled && "opacity-55")}>
                          <TableCell><code className="text-[11px] font-mono text-foreground/80">{d.matcher}</code></TableCell>
                          <TableCell className="text-xs text-muted-foreground">{d.description || ""}</TableCell>
                          <TableCell>
                            <div className="flex flex-wrap items-center gap-y-0.5">
                              {cats.length ? cats.map((c: string) => <Pill key={c}>{c}</Pill>) : <Muted />}
                              {overridden && <span className="text-[10px] text-amber-500 ml-1" title="Remapped by an admin">· remapped</span>}
                            </div>
                          </TableCell>
                          <TableCell>{sensVal && sensVal !== "-" ? <GatewaySensitivityBadge value={sensVal} showUnknown /> : <Muted />}</TableCell>
                          <TableCell><Toggle on={!!d.enabled} onClick={() => toggleDetector(d.matcher, !d.enabled)} /></TableCell>
                          <TableCell className="text-right whitespace-nowrap">
                            <div className="inline-flex items-center justify-end gap-1">
                              <button
                                type="button"
                                title="Override category / sensitivity"
                                onClick={() => setOverride({ matcher: d.matcher, detector: d })}
                                className="inline-flex items-center gap-1 rounded-full border border-primary/30 bg-primary/10 text-primary text-[11px] font-medium px-2 py-0.5 hover:bg-primary/20 transition-colors"
                              >
                                <SlidersHorizontal className="w-3 h-3" /> Override
                              </button>
                              {overridden && <IconBtn title="Clear override (revert to default)" onClick={() => clearOverride(d.matcher)}><RotateCcw className="w-3.5 h-3.5" /></IconBtn>}
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </>
          )}
        </>
      )}

      <RuleFormDialog open={formRule.open} rule={formRule.rule} onClose={() => setFormRule({ open: false, rule: null })} onSaved={load} />
      <RuleAssistantDialog open={assistant.open} editRule={assistant.rule} onClose={() => setAssistant({ open: false, rule: null })} onSaved={load} />
      <TemplateLibraryDialog open={templatesOpen} onClose={() => setTemplatesOpen(false)} onChanged={load} />
      <OverrideDialog matcher={override.matcher} detector={override.detector} onClose={() => setOverride({ matcher: null, detector: null })} onSaved={load} />
      <ConfirmDialog state={confirmState} onClose={() => setConfirmState(null)} />
    </div>
  );
}

// ═══════════════════════════ Page ═══════════════════════════

type PPTab = "classifications" | "insights" | "rules";

export default function GatewayPostProcessorPage() {
  const [params] = useSearchParams();
  const initial = useMemo(() => ({ correlationId: params.get("correlationId") || "", traceId: params.get("traceId") || "" }), [params]);
  const [tab, setTab] = useState<PPTab>("classifications");

  const tabs: { id: PPTab; label: string }[] = [
    { id: "classifications", label: "Classifications" },
    { id: "insights", label: "Insights" },
    { id: "rules", label: "Rules" },
  ];

  return (
    <div className="ag-page">
      <div className="ag-card ag-toolbar ag-nav">
        <div className="cea-tabs ag-tabs" role="tablist" aria-label="Processed data views">
          {tabs.map((t) => (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={tab === t.id}
              onClick={() => setTab(t.id)}
              className={cn("cea-tab", tab === t.id && "active")}
            >
              {t.label}
            </button>
          ))}
        </div>
        <span className="ag-summary" title="Egress post-processor — what kind of data each governed response carried: categories, sensitivity, detector evidence, and injection flags.">
          <ShieldCheck aria-hidden="true" />
          <span>Egress post-processor</span>
        </span>
      </div>

      <div className="ag-card ag-content">
        <div className="ag-scroll">
          {tab === "classifications" ? <ClassificationsTab initial={initial} /> : tab === "insights" ? <InsightsTab /> : <RulesTab />}
        </div>
      </div>
    </div>
  );
}
